
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ExportVideoResponse {
  message: string;
  status: string;
  projectId?: string;
  path?: string;
}

interface ExportVideoError {
  error: {
    message: string;
    status?: string;
  };
}

export const useVideoExport = () => {
  const queryClient = useQueryClient();

  const exportVideoMutation = useMutation({
    mutationFn: async (projectId: string): Promise<ExportVideoResponse> => {
      console.log('🎬 Starting video export for project:', projectId);
      
      const { data, error } = await supabase.functions.invoke('request-project-video-export', {
        body: { projectId }
      });

      if (error) {
        console.error('❌ Export function error:', error);
        throw error;
      }

      // Handle both success and error responses from the edge function
      if (data.error) {
        const exportError = data as ExportVideoError;
        throw new Error(exportError.error.message);
      }

      return data as ExportVideoResponse;
    },
    onSuccess: (data) => {
      console.log('✅ Video export initiated:', data);
      
      if (data.status === 'completed') {
        toast.success('Video export already completed!');
      } else {
        toast.success('Video export started! We\'ll notify you when it\'s ready.');
      }
      
      // Invalidate project query to refresh export status
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
    onError: (error: Error) => {
      console.error('❌ Video export failed:', error);
      toast.error(`Export failed: ${error.message}`);
    },
  });

  return {
    exportVideo: exportVideoMutation.mutate,
    isExporting: exportVideoMutation.isPending,
    exportError: exportVideoMutation.error,
  };
};
